import React, { useCallback, useEffect, useRef, useState } from 'react';
import { trackMiniAppEvent } from '../../utils/analytics';
import {
  getEngineRootStyle,
  titleStyle,
  instructionsStyle,
  primaryBtn,
  secondaryBtn,
  feedbackCorrect,
  feedbackIncorrect,
} from './shared/engineTheme';
import { useCelebration, CelebrationOverlay, A11yAnnouncement } from './shared/Celebration';
import { useGuidedHints } from './shared/useGuidedHints';
import GuidedHintOverlay, {
  GuidedHintHelpButton,
  guidedHintReadAloud,
} from './shared/GuidedHintOverlay';
import { isTapFirstLessonUi } from './shared/lessonUiMode';
import { shouldSuppressActivityTitle } from './shared/guidedPromptUtils';

/**
 * Pattern continuation (numeric or string tokens).
 * Content: {
 *   instructions?,
 *   sequence: any[],
 *   choices: [{ id, text, correct? }],
 *   missingIndex?: number,      // show "?" here instead of only at end
 *   ruleLabel?: string,
 *   correctChoiceId?: string,   // alternative to marking choice.correct
 *   explanation?,
 * }
 *
 * Guided shell (`onRegisterLessonFooterActions`): select a choice, then footer **Check** — grading and green/red feedback only after Check (demo / no shell: tap still checks immediately). Footer **Show answer** reveals the choice but **Continue** finishes the step so learners can read the solution first.
 */
export default function PatternEngine({
  title = 'Pattern',
  content = {},
  hints = [],
  explanation,
  activityId = 'engine_pattern',
  onProgress,
  onComplete,
  guidedHintsEnabled = true,
  onRegisterLessonFooterActions,
  lessonUiMode = 'default',
  guidedContext,
}) {
  const tapFirst = isTapFirstLessonUi(lessonUiMode);
  const singleSurface = guidedContext?.singleSurface === true;
  const stepTitle = typeof guidedContext?.stepTitle === 'string' ? guidedContext.stepTitle : '';
  const suppressActivityTitle = shouldSuppressActivityTitle({ stepTitle, activityTitle: title });
  const sequence = content.sequence || [];
  const choices = content.choices || [];
  const missingIndex =
    content.missingIndex != null && Number.isFinite(Number(content.missingIndex))
      ? Math.max(0, Math.min(sequence.length - 1, Math.floor(Number(content.missingIndex))))
      : null;
  const ruleLabel = typeof content.ruleLabel === 'string' ? content.ruleLabel.trim() : '';

  const isChoiceCorrect = (choice) => {
    if (content.correctChoiceId != null && String(content.correctChoiceId).trim() !== '') {
      return choice?.id === content.correctChoiceId;
    }
    return !!choice?.correct;
  };

  /** Lesson shell: pick an option, then footer Check (same contract as MultipleChoiceEngine). */
  const shellDeferredCheck = typeof onRegisterLessonFooterActions === 'function';

  const [picked, setPicked] = useState(null);
  /** After a wrong Check in the shell, keep `phase` at `pick` and show neutral wrong styling until the learner picks again. */
  const [shellWrongReveal, setShellWrongReveal] = useState(null);
  const [phase, setPhase] = useState('pick');
  const [completed, setCompleted] = useState(false);
  /** Shell “Show answer” — board shows the solution; wait for Continue before `onComplete`. */
  const [awaitContinueAfterRevealAnswer, setAwaitContinueAfterRevealAnswer] = useState(false);
  const patternLessonCompleteSentRef = useRef(false);
  const [showCelebration, celebrate] = useCelebration();
  const [a11yMsg, setA11yMsg] = useState('');

  const getExplanation = useCallback(() => content.explanation, [content.explanation]);

  const guided = useGuidedHints({
    activityId,
    hints,
    explanation,
    getExplanation,
    isActivityComplete: completed && !awaitContinueAfterRevealAnswer,
    enabled: guidedHintsEnabled,
  });
  const { noteUserAction, noteCorrectAnswer, noteIncorrectAnswer } = guided;

  /** Tap-first guided shell: tighter vertical rhythm so sequence + choices fit above the footer. */
  const compactTap = tapFirst && singleSurface;
  const narrowTap = compactTap && guided.isNarrow;

  const pickChoice = (choiceId) => {
    if (phase !== 'pick') return;
    const choice = choices.find((c) => c.id === choiceId);
    if (!choice) return;
    if (shellDeferredCheck) {
      setShellWrongReveal(null);
      setPicked(choiceId);
      return;
    }
    noteUserAction();
    setPicked(choiceId);
    setPhase('reveal');
    const ok = isChoiceCorrect(choice);
    setA11yMsg(ok ? 'Correct!' : 'Try again.');
    onProgress?.({ activityId, engine: 'pattern', phase: 'answer', lastCorrect: ok });
    if (ok && !completed) {
      setCompleted(true);
      celebrate();
      noteCorrectAnswer();
      trackMiniAppEvent.interaction(activityId, 'correct');
      if (!patternLessonCompleteSentRef.current) {
        patternLessonCompleteSentRef.current = true;
        onComplete?.({ activityId, engine: 'pattern', perfect: true });
        trackMiniAppEvent.completed(activityId);
      }
    } else if (!ok) {
      noteIncorrectAnswer();
      trackMiniAppEvent.interaction(activityId, 'incorrect');
    }
  };

  const commitFromFooter = useCallback(() => {
    if (!shellDeferredCheck || phase !== 'pick' || picked == null) return;
    const choice = choices.find((c) => c.id === picked);
    if (!choice) return;
    noteUserAction();
    const ok = isChoiceCorrect(choice);
    setA11yMsg(ok ? 'Correct!' : 'Try again.');
    onProgress?.({ activityId, engine: 'pattern', phase: 'answer', lastCorrect: ok });
    if (ok && !completed) {
      setPhase('reveal');
      setCompleted(true);
      celebrate();
      noteCorrectAnswer();
      trackMiniAppEvent.interaction(activityId, 'correct');
      if (!patternLessonCompleteSentRef.current) {
        patternLessonCompleteSentRef.current = true;
        onComplete?.({ activityId, engine: 'pattern', perfect: true });
        trackMiniAppEvent.completed(activityId);
      }
    } else {
      setShellWrongReveal({ wrongChoiceId: picked });
      setPicked(null);
      noteIncorrectAnswer();
      trackMiniAppEvent.interaction(activityId, 'incorrect');
    }
  }, [
    shellDeferredCheck,
    phase,
    picked,
    choices,
    completed,
    content,
    noteUserAction,
    noteCorrectAnswer,
    noteIncorrectAnswer,
    celebrate,
    activityId,
    onProgress,
    onComplete,
  ]);

  const tryAgain = () => {
    noteUserAction();
    setPicked(null);
    setShellWrongReveal(null);
    setPhase('pick');
  };

  const revealAnswer = useCallback(() => {
    if (completed) return;
    const correct = choices.find((c) => isChoiceCorrect(c));
    if (!correct) return;
    noteUserAction();
    setShellWrongReveal(null);
    if (phase === 'reveal') {
      const sel = choices.find((c) => c.id === picked);
      if (sel && isChoiceCorrect(sel)) return;
      setPicked(null);
      setPhase('pick');
    }
    setPicked(correct.id);
    setPhase('reveal');
    setCompleted(true);
    celebrate();
    noteCorrectAnswer();
    trackMiniAppEvent.interaction(activityId, 'reveal_answer');
    onProgress?.({ activityId, engine: 'pattern', phase: 'answer', lastCorrect: true });
    if (shellDeferredCheck) {
      setAwaitContinueAfterRevealAnswer(true);
      setA11yMsg('Answer shown. Tap Continue when you are ready.');
      return;
    }
    if (!patternLessonCompleteSentRef.current) {
      patternLessonCompleteSentRef.current = true;
      onComplete?.({ activityId, engine: 'pattern', perfect: false, revealed: true });
      trackMiniAppEvent.completed(activityId);
    }
  }, [
    completed,
    choices,
    phase,
    picked,
    content,
    noteUserAction,
    noteCorrectAnswer,
    celebrate,
    activityId,
    onProgress,
    onComplete,
    shellDeferredCheck,
  ]);

  useEffect(() => {
    if (!onRegisterLessonFooterActions) return undefined;
    if (sequence.length === 0 || choices.length === 0) {
      onRegisterLessonFooterActions(null);
      return undefined;
    }
    if (shellDeferredCheck && completed && awaitContinueAfterRevealAnswer) {
      onRegisterLessonFooterActions({
        continueLesson: () => {
          noteUserAction();
          setAwaitContinueAfterRevealAnswer(false);
          if (patternLessonCompleteSentRef.current) return;
          patternLessonCompleteSentRef.current = true;
          onComplete?.({ activityId, engine: 'pattern', perfect: false, revealed: true });
          trackMiniAppEvent.completed(activityId);
        },
      });
      return () => onRegisterLessonFooterActions(null);
    }
    if (completed) {
      onRegisterLessonFooterActions(null);
      return undefined;
    }
    if (shellDeferredCheck) {
      if (phase === 'pick') {
        onRegisterLessonFooterActions({
          checkAnswer: commitFromFooter,
          checkAnswerDisabled: picked == null,
          showAnswer: revealAnswer,
        });
        return () => onRegisterLessonFooterActions(null);
      }
      onRegisterLessonFooterActions({ showAnswer: revealAnswer });
      return () => onRegisterLessonFooterActions(null);
    }
    onRegisterLessonFooterActions({ showAnswer: revealAnswer });
    return () => onRegisterLessonFooterActions(null);
  }, [
    onRegisterLessonFooterActions,
    completed,
    awaitContinueAfterRevealAnswer,
    revealAnswer,
    commitFromFooter,
    shellDeferredCheck,
    phase,
    picked,
    sequence.length,
    choices.length,
    noteUserAction,
    activityId,
    onComplete,
  ]);

  if (sequence.length === 0 || choices.length === 0) {
    return (
      <div style={getEngineRootStyle({ singleSurface })}>
        <p style={instructionsStyle}>No pattern in this activity yet.</p>
      </div>
    );
  }

  const selected = choices.find((c) => c.id === picked);
  const wasCorrect = selected ? isChoiceCorrect(selected) : false;

  return (
    <div
      style={{
        ...getEngineRootStyle({ singleSurface }),
        position: 'relative',
        ...(guided.contentReserveStyle || {}),
        ...(tapFirst ? { overflow: 'hidden', minHeight: 0 } : {}),
      }}
    >
      <CelebrationOverlay visible={showCelebration} message="Pattern power!" />
      <A11yAnnouncement message={a11yMsg} />
      <GuidedHintOverlay
        open={guided.open}
        isNarrow={guided.isNarrow}
        visibleLevel={guided.visibleLevel}
        maxLevel={guided.maxLevel}
        isInteractionPhase={guided.isInteractionPhase}
        anotherClueEnabled={guided.anotherClueEnabled}
        hintEntry={guided.hintEntry}
        showHowAvailable={guided.showHowAvailable}
        resolvedExplanation={guided.resolvedExplanation}
        explanationOpen={guided.explanationOpen}
        onDismiss={guided.dismissOverlay}
        onAnotherClue={guided.anotherClue}
        onReadAloud={() =>
          guidedHintReadAloud(guided.hintEntry, activityId, guided.readAloudTrackLevel)
        }
        onShowHow={guided.openExplanation}
      />
      <GuidedHintHelpButton visible={guided.showHelpButton} onClick={guided.requestHintManually} />

      {!suppressActivityTitle ? (
        <h2 style={{ ...titleStyle, margin: compactTap ? '0 0 4px' : titleStyle.margin }}>{title}</h2>
      ) : null}
      {!tapFirst && content.instructions && <p style={instructionsStyle}>{content.instructions}</p>}
      {ruleLabel ? (
        <p
          style={{
            ...instructionsStyle,
            fontWeight: 800,
            marginTop: 0,
            marginBottom: compactTap ? 6 : 10,
            fontSize: compactTap ? 14 : instructionsStyle.fontSize,
            lineHeight: compactTap ? 1.35 : instructionsStyle.lineHeight,
          }}
        >
          {ruleLabel}
        </p>
      ) : null}

      <div
        role="group"
        aria-label="Pattern sequence"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: compactTap ? 6 : 10,
          alignItems: 'center',
          marginBottom: compactTap ? 10 : 18,
          padding: compactTap ? (narrowTap ? '8px 8px' : '10px 10px') : '14px 12px',
          borderRadius: compactTap ? 12 : 16,
          background: singleSurface ? 'rgba(15, 23, 42, 0.03)' : 'var(--color-bg)',
          border: singleSurface ? 'none' : '1px solid var(--color-border)',
        }}
      >
        {sequence.map((cell, i) => {
          const isMissing = missingIndex != null && i === missingIndex;
          return (
            <span
              key={`${i}-${cell}`}
              style={{
                minWidth: compactTap ? 40 : 44,
                minHeight: compactTap ? 36 : 44,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: compactTap ? '6px 8px' : '8px 12px',
                borderRadius: compactTap ? 10 : 12,
                fontSize: compactTap ? (narrowTap ? 13 : 14) : 18,
                fontWeight: 800,
                background: isMissing ? 'rgba(99,102,241,0.12)' : 'var(--color-bg-card)',
                border: isMissing ? '2px dashed var(--color-primary)' : '2px solid var(--color-border)',
                color: 'var(--color-text)',
              }}
            >
              {isMissing ? '?' : String(cell)}
            </span>
          );
        })}
        {missingIndex == null ? (
          <span
            aria-hidden
            style={{
              fontSize: compactTap ? (narrowTap ? 22 : 24) : 28,
              fontWeight: 900,
              color: 'var(--color-primary)',
              padding: '0 4px',
            }}
          >
            ?
          </span>
        ) : null}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: compactTap ? 6 : 10 }}>
        {choices.map((c, ci) => {
          const revealed = phase === 'reveal';
          const shellRevealWrongOnly = !!(shellDeferredCheck && shellWrongReveal);
          let bg = 'var(--color-bg)';
          if (revealed) {
            if (wasCorrect) {
              if (isChoiceCorrect(c)) bg = 'rgba(34, 197, 94, 0.2)';
              else if (c.id === picked && !isChoiceCorrect(c)) bg = 'rgba(239, 68, 68, 0.15)';
            } else if (c.id === picked && !isChoiceCorrect(c)) {
              bg = 'rgba(239, 68, 68, 0.15)';
            }
          } else if (shellRevealWrongOnly) {
            if (shellWrongReveal?.wrongChoiceId === c.id && !isChoiceCorrect(c)) {
              bg = 'rgba(239, 68, 68, 0.15)';
            }
          } else if (shellDeferredCheck && phase === 'pick' && picked === c.id) {
            bg = 'rgba(139, 92, 246, 0.14)';
          }
          return (
            <button
              key={c.id}
              type="button"
              disabled={phase !== 'pick'}
              onClick={() => pickChoice(c.id)}
              aria-pressed={shellDeferredCheck && phase === 'pick' ? picked === c.id : undefined}
              style={{
                ...secondaryBtn,
                background: bg,
                opacity:
                  revealed && picked != null && c.id !== picked && !isChoiceCorrect(c) ? 0.65 : 1,
                minHeight: tapFirst ? (compactTap ? 46 : 56) : 44,
                padding: tapFirst ? (compactTap ? '10px 12px' : '14px 16px') : '10px 14px',
                fontSize: tapFirst ? (compactTap ? 15 : 16) : 14,
              }}
            >
              <span style={{ marginRight: 8 }} aria-hidden>{String.fromCharCode(65 + ci)}.</span>
              {c.text}
            </button>
          );
        })}
      </div>

      {phase === 'reveal' && (
        <div
          style={{
            ...(wasCorrect ? feedbackCorrect : feedbackIncorrect),
            marginTop: compactTap ? 8 : 14,
          }}
        >
          {wasCorrect ? '✓ That continues the pattern!' : '↻ Another choice might fit better.'}
        </div>
      )}

      {shellDeferredCheck && shellWrongReveal ? (
        <div style={{ ...feedbackIncorrect, marginTop: compactTap ? 8 : 14 }} role="status">
          <span style={{ fontWeight: 800 }}>Not quite.</span>
          <span style={{ display: 'block', marginTop: 6, fontWeight: 600, fontSize: 14 }}>
            That does not continue the pattern. Pick another answer, then tap Check.
          </span>
        </div>
      ) : null}

      {phase === 'reveal' && !wasCorrect && !shellDeferredCheck && (
        <button type="button" style={{ ...primaryBtn, marginTop: 12 }} onClick={tryAgain}>
          Try again
        </button>
      )}

    </div>
  );
}
